import React, { useState } from 'react';
import { FadeIn } from './FadeIn';
import { Shield, Check, Star, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
type Variant = 'primary' | 'final';
const PLANS = [{
  id: 'base',
  name: 'Kit Base',
  tagline: '6 tomos + 6 bonos',
  price: '$19.990',
  priceNumber: 19990,
  originalPrice: null as string | null,
  url: 'https://tumanualdigital.com/cart/44219175370788:1',
  features: ['6 tomos clínicos completos', '6 bonos profesionales incluidos', 'PDF + Word editables']
}, {
  id: 'completo',
  name: 'Kit Completo',
  tagline: 'Kit Base + Pack Expansión',
  price: '$27.990',
  priceNumber: 27990,
  originalPrice: '$38.000',
  url: 'https://tumanualdigital.com/cart/44219220656164:1',
  featured: true,
  features: ['Todo lo del Kit Base', '+ Pack Expansión: exóticos, urgencias y certificados', '+ Modelos de interconsulta entre colegas', '+ Guía de precios anti-inflación + 20 ideas Instagram']
}];
interface PricingSelectorProps {
  variant?: Variant;
  id?: string;
}
export function PricingSelector({
  variant = 'primary',
  id
}: PricingSelectorProps) {
  const [selectedId, setSelectedId] = useState('completo');
  const selected = PLANS.find(p => p.id === selectedId) ?? PLANS[1];
  const isFinal = variant === 'final';
  return <section id={id} className={`py-12 md:py-16 px-4 ${isFinal ? 'bg-brand-dark-green text-brand-cream' : ''}`} data-id="element-86">
      <div className="max-w-4xl mx-auto" data-id="element-87">
        <FadeIn data-id="element-88">
          <div className="text-center mb-6 md:mb-8" data-id="element-89">
            {isFinal && <div className="inline-flex items-center gap-2 bg-brand-gold/20 text-brand-gold px-4 py-1.5 rounded-full font-bold border border-brand-gold/40 text-xs md:text-sm mb-4" data-id="element-90">
                <Sparkles size={14} data-id="element-91" /> ÚLTIMA OPORTUNIDAD EN ESTA PÁGINA
              </div>}
            <h2 className={`text-2xl md:text-4xl font-black mb-2 ${isFinal ? 'text-brand-white' : 'text-brand-dark'}`} data-id="element-92">
              {isFinal ? 'Elegí tu kit antes de cerrar' : 'Elegí tu kit y empezá hoy'}
            </h2>
            <p className={`text-sm md:text-base font-medium ${isFinal ? 'text-brand-cream/70' : 'text-brand-muted'}`} data-id="element-93">
              Tocá la opción que querés y presioná comprar
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-94">
          <div className={`rounded-2xl md:rounded-3xl p-4 md:p-6 ${isFinal ? 'bg-brand-dark/40 border border-brand-gold/30' : 'bg-brand-white border-2 border-brand-border shadow-[0_15px_40px_rgba(20,83,45,0.08)]'}`} data-id="element-95">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-5 md:mb-6" data-id="element-96">
              {PLANS.map(plan => {
              const isSelected = plan.id === selectedId;
              const isPlanFeatured = plan.featured;
              return <button key={plan.id} type="button" onClick={() => setSelectedId(plan.id)} aria-pressed={isSelected} className={`relative text-left rounded-xl md:rounded-2xl p-4 md:p-5 transition-all duration-200 ${isSelected ? isPlanFeatured ? isFinal ? 'bg-brand-gold/15 border-2 border-brand-gold shadow-[0_0_25px_rgba(201,169,97,0.3)] scale-[1.02]' : 'bg-brand-cream border-2 border-brand-green shadow-[0_8px_25px_rgba(20,83,45,0.15)] scale-[1.02]' : isFinal ? 'bg-brand-white/10 border-2 border-brand-cream/40 scale-[1.01]' : 'bg-brand-cream border-2 border-brand-green/60 scale-[1.01]' : isFinal ? 'bg-brand-white/5 border-2 border-brand-cream/10 opacity-70 hover:opacity-100' : 'bg-brand-white border-2 border-brand-border opacity-75 hover:opacity-100'}`} data-id="element-97">
                    {isPlanFeatured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-gold text-brand-dark font-black px-3 py-0.5 rounded-full text-[10px] md:text-xs flex items-center gap-1 whitespace-nowrap shadow-md" data-id="element-98">
                        <Star className="w-3 h-3" fill="currentColor" data-id="element-99" /> MÁS
                        ELEGIDA
                      </div>}

                    {/* Radio indicator */}
                    <div className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? isPlanFeatured ? 'bg-brand-gold border-brand-gold' : 'bg-brand-green border-brand-green' : isFinal ? 'bg-transparent border-brand-cream/40' : 'bg-brand-white border-brand-border'}`} data-id="element-100">
                      {isSelected && <Check className={`w-3 h-3 ${isPlanFeatured ? 'text-brand-dark' : 'text-brand-white'}`} strokeWidth={4} data-id="element-101" />}
                    </div>

                    <div className="pr-7" data-id="element-102">
                      <h3 className={`text-sm md:text-base font-black mb-1 leading-tight ${isFinal ? 'text-brand-white' : 'text-brand-dark'}`} data-id="element-103">
                        {plan.name}
                      </h3>
                      <p className={`text-[11px] md:text-xs font-bold mb-3 ${isFinal ? 'text-brand-cream/70' : 'text-brand-muted'}`} data-id="element-104">
                        {plan.tagline}
                      </p>

                      <div className="flex items-baseline gap-2 mb-3" data-id="element-105">
                        {plan.originalPrice && <span className={`line-through text-xs font-bold ${isFinal ? 'text-brand-cream/50' : 'text-brand-muted'}`} data-id="element-106">
                            {plan.originalPrice}
                          </span>}
                        <span className={`text-2xl md:text-3xl font-black ${isPlanFeatured ? 'text-brand-gold' : isFinal ? 'text-brand-white' : 'text-brand-green'}`} data-id="element-107">
                          {plan.price}
                        </span>
                        <span className={`text-[10px] md:text-xs font-bold ${isFinal ? 'text-brand-cream/60' : 'text-brand-muted'}`} data-id="element-108">
                          ARS
                        </span>
                      </div>

                      <ul className="space-y-1.5" data-id="element-109">
                        {plan.features.map((f, i) => <li key={i} className={`flex items-start gap-1.5 text-[11px] md:text-xs font-medium ${isFinal ? 'text-brand-cream/85' : 'text-brand-dark'}`} data-id="element-110">
                            <Check className={`mt-0.5 shrink-0 ${isPlanFeatured ? 'text-brand-gold' : isFinal ? 'text-brand-cream/70' : 'text-brand-green'}`} size={12} strokeWidth={3} data-id="element-111" />
                            <span data-id="element-112">{f}</span>
                          </li>)}
                      </ul>
                    </div>
                  </button>;
            })}
            </div>

            {/* Shared CTA */}
            <motion.a key={selected.id} initial={{
            opacity: 0.7,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} href={selected.url} className={`w-full block text-center py-4 md:py-5 px-4 rounded-xl font-black text-base md:text-lg transition-colors shadow-lg mb-4 ${selected.featured ? 'bg-brand-gold hover:bg-brand-gold-dark text-brand-dark' : 'bg-brand-green hover:bg-brand-green-hover text-brand-white'}`} data-id="element-113">
              → SÍ, QUIERO {selected.name.toUpperCase()} · {selected.price} ARS
            </motion.a>

            <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-xs md:text-sm font-medium" data-id="element-114">
              <span className={isFinal ? 'text-brand-cream/70' : 'text-brand-muted'} data-id="element-115">
                Pago único · Descarga inmediata · Sin suscripción
              </span>
              <span className={`flex items-center gap-1.5 font-bold ${isFinal ? 'text-brand-gold' : 'text-brand-green'}`} data-id="element-116">
                <Shield size={14} data-id="element-117" /> Garantía 7 días
              </span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}